import { useEffect, useState } from 'react';
import { Route, Routes, useParams } from 'react-router-dom';
import { api, type SiteBundle } from '../api';
import { run, useAsync, useToast } from '../hooks';
import { ExperimentPanel } from '../components/ExperimentPanel';
import { AppearanceForm } from '../components/AppearanceForm';
import { GoalsEditor } from '../components/GoalsEditor';
import { Overview } from '../components/Overview';
import { PagesTable } from '../components/PagesTable';
import { QuestionsEditor } from '../components/QuestionsEditor';
import { SpringboardEditor } from '../components/SpringboardEditor';
import { StatusBadge } from '../components/StatusBadge';

export function SitePage() {
  const { siteId = '' } = useParams();
  const { data, error, loading, reload } = useAsync(() => api.site(siteId), [siteId]);
  const { toast, show } = useToast();
  const [summary, setSummary] = useState('');

  // Poll while a scan is running so the header and editors refresh on completion.
  const running = data?.scans.some((s) => !['ready', 'failed'].includes(s.status)) || data?.site.status === 'scanning';
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => void reload(), 3000);
    return () => clearInterval(t);
  }, [running, reload]);

  // While switching sites the previous bundle is still in hand; never hand it to editors for the new site.
  if ((loading && !data) || (data && data.site.siteId !== siteId)) return <p className="muted">Loading…</p>;
  if (error || !data) return <p className="badge bad">{error ?? 'Not found'}</p>;
  const { site } = data;
  const hasDraft = !!site.draftConfigVersion;

  const publish = async () => {
    const r = await run(api.publish(siteId, summary || 'Published from console'), show, 'Published');
    if (r) {
      setSummary('');
      await reload();
    }
  };

  return (
    <div key={siteId}>
      <div className="topbar">
        <div>
          <div className="row">
            <h1>{site.brandName || site.domain}</h1>
            <StatusBadge status={site.status} />
            {site.publishedConfigVersion && <span className="badge accent">published v{site.publishedConfigVersion}</span>}
            {hasDraft && <span className="badge warn">draft v{site.draftConfigVersion}</span>}
          </div>
          <p className="muted small">
            <a href={site.primaryUrl} target="_blank" rel="noopener">
              {site.primaryUrl}
            </a>{' '}
            · key <span className="mono">{site.siteKey}</span>
          </p>
        </div>
        <div className="row">
          <input className="input" style={{ width: 240 }} placeholder="What changed? (optional)" value={summary} onChange={(e) => setSummary(e.target.value)} />
          <button className="btn primary" onClick={publish} disabled={running}>
            Publish changes
          </button>
        </div>
      </div>


      <Routes>
        <Route index element={<Overview bundle={data} reload={reload} show={show} />} />
        <Route path="springboard" element={<SpringboardEditor bundle={data} reload={reload} show={show} />} />
        <Route path="questions" element={<QuestionsEditor bundle={data} reload={reload} show={show} />} />
        <Route path="goals" element={<GoalsEditor bundle={data} reload={reload} show={show} />} />
        <Route path="appearance" element={<AppearanceForm bundle={data} reload={reload} show={show} />} />
        <Route path="pages" element={<PagesTable bundle={data} reload={reload} show={show} />} />
        <Route path="experiment" element={<ExperimentPanel bundle={data} reload={reload} show={show} />} />
      </Routes>
      {toast && <div className={`toast${toast.err ? ' err' : ''}`}>{toast.text}</div>}
    </div>
  );
}

export type EditorProps = { bundle: SiteBundle; reload: () => Promise<void>; show: (t: string, err?: boolean) => void };
